import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../app/store';
import { updateProfile } from './authSlice';
import { updatePasswordApi } from '../../services/api';
import toast from 'react-hot-toast';

const ProfilePage: React.FC = () => {
  const dispatch: AppDispatch = useDispatch();
  const { user } = useSelector((state: RootState) => state.auth);

  const [name, setName] = useState(user?.name || '');
  const [email, setEmail] = useState(user?.email || '');
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileSaved, setProfileSaved] = useState(false);

  const [currentPassword, setCurrentPassword] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordSaved, setPasswordSaved] = useState(false);

  useEffect(() => {
    if (user) {
      setName(user.name);
      setEmail(user.email);
    }
  }, [user]);

  const canManageDelegations = user && !user.is_admin && [4, 5, 6, 8].includes(user.user_role_id || 0);
  const isApprover = user && [2, 3].includes(user.user_role_id || 0);

  const handleProfileSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setProfileLoading(true);
    setProfileSaved(false);

    try {
      await dispatch(updateProfile({ name, email })).unwrap();
      setProfileSaved(true);
      toast.success('Profile updated successfully.');
      setTimeout(() => setProfileSaved(false), 3000);
    } catch (error: any) {
      toast.error(typeof error === 'string' ? error : 'Failed to update profile.');
    } finally {
      setProfileLoading(false);
    }
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordLoading(true);
    setPasswordSaved(false);

    try {
      await updatePasswordApi({
        current_password: currentPassword,
        password,
        password_confirmation: passwordConfirmation,
      });
      setPasswordSaved(true);
      toast.success('Password updated successfully.');
      setCurrentPassword('');
      setPassword('');
      setPasswordConfirmation('');
      setTimeout(() => setPasswordSaved(false), 3000);
    } catch (error: any) {
      const message = error.response?.data?.message || error.message || 'Failed to update password.';
      toast.error(message);
    } finally {
      setPasswordLoading(false);
    }
  };

  const sectionClass = 'bg-white dark:bg-midnight-800/40 border border-slate-200 dark:border-white/5 rounded-xl p-6 shadow-sm';
  const labelClass = 'block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5';
  const inputClass = 'w-full rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-midnight-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-500/50 focus:border-brand-500 disabled:opacity-60 disabled:cursor-not-allowed';
  const buttonClass = 'inline-flex items-center justify-center px-4 py-2 bg-brand-600 hover:bg-brand-700 text-white font-medium rounded-lg transition-all text-sm disabled:opacity-50 disabled:cursor-not-allowed';

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div className="flex items-center gap-4 mb-6">
        <div className="w-16 h-16 rounded-full bg-accent-500/15 border border-accent-500/30 flex items-center justify-center">
          <span className="text-accent-700 dark:text-accent-200 font-bold text-2xl">
            {user?.name?.charAt(0).toUpperCase() || 'U'}
          </span>
        </div>
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Profile</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Manage your account settings and preferences.
          </p>
        </div>
      </div>

      {canManageDelegations && (
        <div className={sectionClass}>
          <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-1">PO Delegations</h2>
          <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
            Manage who can act on your purchase orders on your behalf.
          </p>
          <a
            href="/profile/delegations"
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-all text-sm"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
            Manage My Delegations
          </a>
        </div>
      )}

      <div className={sectionClass}>
        <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-1">Profile Information</h2>
        <p className="text-sm text-slate-500 dark:text-slate-400 mb-6">
          Update your account's profile information and email address.
        </p>

        <form onSubmit={handleProfileSubmit} className="space-y-4">
          <div>
            <label htmlFor="name" className={labelClass}>Name</label>
            <input
              id="name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              className={inputClass}
            />
          </div>

          <div>
            <label htmlFor="email" className={labelClass}>Email</label>
            <input
              id="email"
              type="email"
              value={email}
              disabled
              className={inputClass}
            />
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">Email address cannot be changed.</p>
          </div>

          <div className="flex items-center gap-4 pt-2">
            <button
              type="submit"
              disabled={profileLoading || name === user?.name}
              className={buttonClass}
            >
              {profileLoading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </button>
            {profileSaved && (
              <span className="text-sm text-emerald-600 dark:text-emerald-400 font-medium">Saved.</span>
            )}
          </div>
        </form>
      </div>

      <div className={sectionClass}>
        <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-1">Update Password</h2>
        <p className="text-sm text-slate-500 dark:text-slate-400 mb-6">
          Ensure your account is using a long, random password to stay secure.
        </p>

        <form onSubmit={handlePasswordSubmit} className="space-y-4">
          <div>
            <label htmlFor="current_password" className={labelClass}>Current Password</label>
            <input
              id="current_password"
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              required
              autoComplete="current-password"
              className={inputClass}
            />
          </div>

          <div>
            <label htmlFor="password" className={labelClass}>New Password</label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="new-password"
              className={inputClass}
            />
          </div>

          <div>
            <label htmlFor="password_confirmation" className={labelClass}>Confirm Password</label>
            <input
              id="password_confirmation"
              type="password"
              value={passwordConfirmation}
              onChange={(e) => setPasswordConfirmation(e.target.value)}
              required
              autoComplete="new-password"
              className={inputClass}
            />
          </div>

          <div className="flex items-center gap-4 pt-2">
            <button
              type="submit"
              disabled={passwordLoading || !currentPassword || !password || !passwordConfirmation}
              className={buttonClass}
            >
              {passwordLoading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Saving...
                </>
              ) : (
                'Save Password'
              )}
            </button>
            {passwordSaved && (
              <span className="text-sm text-emerald-600 dark:text-emerald-400 font-medium">Saved.</span>
            )}
          </div>
        </form>
      </div>

      {isApprover && (
        <div className={sectionClass}>
          <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-1">Signature</h2>
          <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
            Manage your approval signature used on purchase orders.
          </p>
          <a
            href="/profile"
            className="inline-flex items-center gap-2 px-4 py-2 bg-slate-100 dark:bg-midnight-900 hover:bg-slate-200 dark:hover:bg-midnight-700 text-slate-900 dark:text-slate-100 font-medium rounded-lg transition-all text-sm"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
            </svg>
            Manage Signature on Web App
          </a>
        </div>
      )}
    </div>
  );
};

export default ProfilePage;
